import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';

interface Interaction {
  id: string;
  deal_state: string;
  platform: string;
  context_synced: boolean;
  lead_responded: boolean;
  created_at: string;
}

interface AiDrafts {
  responses?: { tone?: string; text?: string }[];
}

interface DraftRow {
  interaction_id: string | null;
  ai_drafts: AiDrafts | null;
  options_used: { tone?: string; length?: string; goal?: string } | null;
  selected_draft_index: number | null;
  is_sent: boolean | null;
  created_at: string;
}

/** "What's winning" — the dynamic tone/play label of the draft the rep actually chose,
 *  cross-referenced against whether that lead then replied. (Feature #2) */
interface WinningPlay {
  tone: string;
  selected: number;   // times this play's draft was the chosen one
  sent: number;       // of those, how many were injected/sent
  replied: number;    // of those, how many leads then replied
}

function computeWinningPlays(drafts: DraftRow[], interactions: Interaction[]): WinningPlay[] {
  const repliedByInteraction = new Map<string, boolean>();
  interactions.forEach(i => repliedByInteraction.set(i.id, i.lead_responded));

  const byTone: Record<string, WinningPlay> = {};
  drafts.forEach(d => {
    const idx = d.selected_draft_index;
    if (typeof idx !== 'number' || idx < 0) return;
    // The dynamic play label lives in ai_drafts.responses[selectedIdx].tone,
    // NOT options_used.tone (which is the rep's requested tone preset).
    const tone = d.ai_drafts?.responses?.[idx]?.tone?.trim();
    if (!tone) return;
    // Case/whitespace-insensitive grouping — Claude may emit casing variants of
    // the same play label. Matches normalizeToneLabel in the extension's winRates.
    const key = tone.toLowerCase().replace(/\s+/g, ' ');
    const row = (byTone[key] ??= { tone, selected: 0, sent: 0, replied: 0 });
    row.selected++;
    if (d.is_sent) {
      row.sent++;
      // Only count a reply against drafts that were actually sent, so
      // replyPct = replied/sent can never exceed 100%.
      if (d.interaction_id && repliedByInteraction.get(d.interaction_id)) row.replied++;
    }
  });

  return Object.values(byTone).sort((a, b) => b.selected - a.selected);
}

const DEAL_STATES = ['Discovery', 'Pitch', 'Stalled', 'Negotiation', 'Closing'];
const STATE_CHIP: Record<string, string> = {
  Discovery:   'chip-discovery',
  Pitch:       'chip-pitch',
  Stalled:     'chip-stalled',
  Negotiation: 'chip-negotiation',
  Closing:     'chip-closing',
};

function lastNDays(n: number): string[] {
  const days: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

function computeCoachInsight(
  interactions: Interaction[],
  drafts: DraftRow[],
): { highlight: string; text: string } {
  if (interactions.length < 5) {
    const remaining = Math.max(0, 5 - interactions.length);
    return {
      highlight: 'Getting started',
      text: `Analyze ${remaining} more lead${remaining !== 1 ? 's' : ''} to unlock your first performance insight. Every session feeds your personal coaching engine.`,
    };
  }

  const stateStats: Record<string, { total: number; responded: number }> = {};
  DEAL_STATES.forEach(s => { stateStats[s] = { total: 0, responded: 0 }; });
  interactions.forEach(i => {
    if (i.deal_state && stateStats[i.deal_state]) {
      stateStats[i.deal_state].total++;
      if (i.lead_responded) stateStats[i.deal_state].responded++;
    }
  });

  const ratedStates = DEAL_STATES
    .map(s => ({ state: s, ...stateStats[s], rate: stateStats[s].total >= 3 ? stateStats[s].responded / stateStats[s].total : -1 }))
    .filter(s => s.rate >= 0);

  const best  = [...ratedStates].sort((a, b) => b.rate - a.rate)[0];
  const worst = [...ratedStates].sort((a, b) => a.rate - b.rate)[0];

  const draftWins = [0, 0, 0];
  let totalDraftSelections = 0;
  drafts.forEach(d => {
    if (typeof d.selected_draft_index === 'number' && d.selected_draft_index >= 0 && d.selected_draft_index < 3) {
      draftWins[d.selected_draft_index]++;
      totalDraftSelections++;
    }
  });

  const syncedPct = interactions.length > 0 ? interactions.filter(i => i.context_synced).length / interactions.length : 0;

  if (totalDraftSelections >= 5) {
    const maxWins = Math.max(...draftWins);
    const maxIdx = draftWins.indexOf(maxWins);
    const winRate = Math.round((maxWins / totalDraftSelections) * 100);
    if (winRate >= 50) {
      const labels = ['opening hook', 'value anchor', 'Deal Sealer'];
      return {
        highlight: `Draft ${maxIdx + 1} wins ${winRate}% of the time`,
        text: `You consistently reach for the ${labels[maxIdx]} approach. That pattern is calibrated signal — your instinct is right. Keep it as your default and use the others to vary your angle.`,
      };
    }
  }

  if (best && best.rate >= 0.25 && best.total >= 3) {
    const pct = Math.round(best.rate * 100);
    const advice: Record<string, string> = {
      Discovery:   'Your openers are landing — first-touch is working.',
      Pitch:       'Prospects are engaging after your pitch. Your framing is on point.',
      Stalled:     "You're reviving deals others would leave behind.",
      Negotiation: "You're closing strong when terms are on the table.",
      Closing:     "You're converting at the critical moment — keep the pressure on.",
    };
    return {
      highlight: `${best.state} leads: ${pct}% response rate`,
      text: `${advice[best.state] || `Your ${best.state} conversations are your strongest stage.`} Spend more time here — this is where you create the most leverage.`,
    };
  }

  if (worst && worst.rate < 0.15 && worst.total >= 5 && worst.rate >= 0) {
    const pct = Math.round(worst.rate * 100);
    return {
      highlight: `${worst.state} leads: ${pct}% response rate`,
      text: `Your ${worst.state} conversations have the lowest response rate of any stage. Try switching to a Pattern Interrupt or Permission Reset approach — the current angle isn't breaking through.`,
    };
  }

  if (syncedPct < 0.70 && interactions.length >= 5) {
    const capturedPct = Math.round(syncedPct * 100);
    return {
      highlight: `${capturedPct}% thread capture`,
      text: `The extension is missing full conversation context on ${100 - capturedPct}% of your analyses. Open leads in their primary view — not a preview panel — for richer, more accurate drafts.`,
    };
  }

  const responded = interactions.filter(i => i.lead_responded).length;
  const overallRate = Math.round((responded / interactions.length) * 100);
  return {
    highlight: `${overallRate}% response rate across ${interactions.length} analyses`,
    text: `${responded} of your ${interactions.length} analyzed leads replied. Your response rate sharpens as your personalized knowledge base grows with each sent reply.`,
  };
}

interface InsightsPageProps {
  userId: string;
}

export const InsightsPage: React.FC<InsightsPageProps> = ({ userId }) => {
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [factsLearned, setFactsLearned] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = () => {
      Promise.all([
        supabase
          .from('interactions')
          .select('id, deal_state, platform, context_synced, lead_responded, created_at')
          .eq('user_id', userId),
        supabase
          .from('draft_history')
          .select('interaction_id, ai_drafts, options_used, selected_draft_index, is_sent, created_at')
          .eq('user_id', userId),
        // Auto-learning knowledge base — durable business facts Salifly has
        // learned from the rep's sent replies (paid plans). head:true returns
        // only the count, no row payload.
        supabase
          .from('user_knowledge_base')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('type', 'business_fact'),
      ]).then(([{ data: iData }, { data: dData }, { count: kbCount }]) => {
        setInteractions((iData as Interaction[]) ?? []);
        setDrafts((dData as DraftRow[]) ?? []);
        setFactsLearned(kbCount ?? 0);
        setLoading(false);
      });
    };

    fetchData();

    const interactionsChannel = supabase
      .channel('insights-interactions')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'interactions', filter: `user_id=eq.${userId}` }, fetchData)
      .subscribe();

    const draftsChannel = supabase
      .channel('insights-drafts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'draft_history', filter: `user_id=eq.${userId}` }, fetchData)
      .subscribe();

    return () => {
      supabase.removeChannel(interactionsChannel);
      supabase.removeChannel(draftsChannel);
    };
  }, [userId]);

  const isEmpty = !loading && interactions.length === 0;

  // ── Computed stats ──────────────────────────────────────────────────────────
  const total     = interactions.length;
  const synced    = interactions.filter(i => i.context_synced).length;
  const responded = interactions.filter(i => i.lead_responded).length;
  const responsePct = total > 0 ? Math.round((responded / total) * 100) : 0;
  const syncedPct   = total > 0 ? Math.round((synced / total) * 100) : 0;

  const platformCounts: Record<string, number> = {};
  interactions.forEach(i => {
    if (i.platform) platformCounts[i.platform] = (platformCounts[i.platform] ?? 0) + 1;
  });
  const topPlatform = Object.entries(platformCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';

  const stateStats = useMemo(() => {
    const stats: Record<string, { total: number; responded: number }> = {};
    DEAL_STATES.forEach(s => { stats[s] = { total: 0, responded: 0 }; });
    interactions.forEach(i => {
      if (i.deal_state && stats[i.deal_state]) {
        stats[i.deal_state].total++;
        if (i.lead_responded) stats[i.deal_state].responded++;
      }
    });
    return stats;
  }, [interactions]);
  const maxStateCount = Math.max(...DEAL_STATES.map(s => stateStats[s].total), 1);

  const toneCounts: Record<string, number> = {};
  const goalCounts: Record<string, number> = {};
  drafts.forEach(d => {
    if (d.options_used?.tone) toneCounts[d.options_used.tone] = (toneCounts[d.options_used.tone] ?? 0) + 1;
    if (d.options_used?.goal) goalCounts[d.options_used.goal] = (goalCounts[d.options_used.goal] ?? 0) + 1;
  });

  const draftWins = [0, 0, 0];
  let totalDraftSelections = 0;
  drafts.forEach(d => {
    if (typeof d.selected_draft_index === 'number' && d.selected_draft_index >= 0 && d.selected_draft_index < 3) {
      draftWins[d.selected_draft_index]++;
      totalDraftSelections++;
    }
  });

  // Activity — last 14 days
  const days = lastNDays(14);
  const sessionsByDay: Record<string, number>  = {};
  const respondedByDay: Record<string, number> = {};
  days.forEach(d => { sessionsByDay[d] = 0; respondedByDay[d] = 0; });
  interactions.forEach(i => {
    const day = i.created_at.slice(0, 10);
    if (sessionsByDay[day] !== undefined) {
      sessionsByDay[day]++;
      if (i.lead_responded) respondedByDay[day]++;
    }
  });
  const maxDay = Math.max(...Object.values(sessionsByDay), 1);

  const coachInsight = useMemo(() => computeCoachInsight(interactions, drafts), [interactions, drafts]);

  // What's winning for you — dynamic play labels of chosen drafts + reply outcome (#2)
  const winningPlays = useMemo(() => computeWinningPlays(drafts, interactions), [drafts, interactions]);
  const topPlaySelections = winningPlays.reduce((s, p) => s + p.selected, 0);

  const responseRateColor = responsePct >= 30
    ? 'var(--accent)'
    : responsePct >= 15
    ? 'var(--warn)'
    : 'var(--fg-1)';

  if (loading) {
    return (
      <main className="main-content">
        <div className="main-container">
          <div className="insights-loading">
            <div className="insights-spinner" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="main-container">
        <div className="insights-hero">
          <h2 className="insights-title">Sales Intelligence</h2>
          <p className="insights-sub">Your coaching engine — every analysis feeds these metrics in real time.</p>
        </div>

        {isEmpty ? (
          <div className="insights-empty">
            <div className="insights-empty-icon">◎</div>
            <p>Analyze leads with your extension to populate your intelligence dashboard.</p>
            <p className="insights-empty-hint">Open Salifly on any CRM, Gmail, or LinkedIn conversation to get started.</p>
          </div>
        ) : (
          <>
            {/* Coach's Take */}
            <div className="insights-coach">
              <span className="insights-coach-eyebrow">Coach's Take</span>
              <p className="insights-coach-highlight">{coachInsight.highlight}</p>
              <p className="insights-coach-text">{coachInsight.text}</p>
            </div>

            {/* KPI Cards */}
            <div className="insights-stat-grid">
              <div className="insights-stat-card">
                <span className="insights-stat-value">{total}</span>
                <span className="insights-stat-label">Total analyses</span>
              </div>
              <div className="insights-stat-card">
                <span className="insights-stat-value" style={{ color: responseRateColor }}>
                  {responsePct}%
                </span>
                <span className="insights-stat-label">Response rate</span>
              </div>
              <div className="insights-stat-card">
                <span className="insights-stat-value">{syncedPct}%</span>
                <span className="insights-stat-label">Thread captured</span>
              </div>
              <div className="insights-stat-card">
                <span className="insights-stat-value" style={{ textTransform: 'capitalize' }}>{topPlatform}</span>
                <span className="insights-stat-label">Top platform</span>
              </div>
              <div className="insights-stat-card">
                <span className="insights-stat-value" style={{ color: factsLearned > 0 ? 'var(--accent)' : 'var(--fg-1)' }}>{factsLearned}</span>
                <span className="insights-stat-label">Facts learned</span>
              </div>
            </div>

            {/* Response rate by deal state */}
            <div className="insights-section">
              <div className="insights-section-header">
                <h3 className="insights-section-heading">Response rate by deal stage</h3>
                <div className="insights-legend">
                  <span className="insights-legend-item"><span className="insights-legend-dot insights-legend-dot--volume" />Volume</span>
                  <span className="insights-legend-item"><span className="insights-legend-dot insights-legend-dot--rate" />Response rate</span>
                </div>
              </div>
              <div className="insights-state-table">
                {DEAL_STATES.map(state => {
                  const { total: stTotal, responded: stResponded } = stateStats[state];
                  const stRate   = stTotal >= 1 ? stResponded / stTotal : 0;
                  const stRatePct = Math.round(stRate * 100);
                  const volumeW  = maxStateCount > 0 ? (stTotal / maxStateCount) * 100 : 0;
                  const rateColor = stRatePct >= 30
                    ? 'var(--accent)'
                    : stRatePct >= 15
                    ? 'var(--warn)'
                    : stTotal === 0
                    ? 'var(--border-2)'
                    : 'var(--terracotta-700)';
                  return (
                    <div key={state} className="insights-state-row">
                      <span className={`insights-state-badge doc-badge ${STATE_CHIP[state]}`}>{state}</span>
                      <div className="insights-state-bars">
                        <div className="insights-state-volume-track">
                          <div className="insights-state-volume-fill" style={{ width: `${volumeW}%` }} />
                        </div>
                        <div className="insights-state-rate-track">
                          {stTotal > 0 && (
                            <div className="insights-state-rate-fill" style={{ width: `${stRatePct}%`, background: rateColor }} />
                          )}
                        </div>
                      </div>
                      <div className="insights-state-meta">
                        <span className="insights-state-count">{stTotal}</span>
                        {stTotal >= 1 && (
                          <span className="insights-state-rate-pct" style={{ color: rateColor }}>{stRatePct}%</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Activity Chart — 14 days */}
            <div className="insights-section">
              <div className="insights-section-header">
                <h3 className="insights-section-heading">Activity — last 14 days</h3>
                <div className="insights-legend">
                  <span className="insights-legend-item"><span className="insights-legend-dot" style={{ background: 'var(--accent)', opacity: 0.3 }} />Analyses</span>
                  <span className="insights-legend-item"><span className="insights-legend-dot" style={{ background: 'var(--accent)' }} />Replied</span>
                </div>
              </div>
              <div className="insights-sparkline insights-sparkline--tall">
                {days.map(day => {
                  const count   = sessionsByDay[day]  ?? 0;
                  const replied = respondedByDay[day] ?? 0;
                  const barH    = maxDay > 0 ? Math.max((count / maxDay) * 100, count > 0 ? 5 : 0) : 0;
                  const repliedH = count > 0 && replied > 0 ? (replied / count) * barH : 0;
                  const label   = new Date(day + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
                  return (
                    <div key={day} className="insights-spark-col">
                      <div className="insights-spark-bar-wrap">
                        <div style={{ position: 'relative', width: '100%', height: `${barH}%` }}>
                          <div className="insights-spark-bar" style={{ height: '100%' }} title={`${count} analyse${count !== 1 ? 's' : ''}`} />
                          {repliedH > 0 && (
                            <div className="insights-spark-bar-replied" style={{ height: `${repliedH}%` }} title={`${replied} replied`} />
                          )}
                        </div>
                      </div>
                      <span className="insights-spark-label">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Draft Selection Distribution */}
            {totalDraftSelections >= 3 && (
              <div className="insights-section">
                <h3 className="insights-section-heading">Draft selection</h3>
                <div className="insights-draft-wins">
                  {(['Opening Hook', 'Value Anchor', 'Deal Sealer'] as const).map((label, idx) => {
                    const wins = draftWins[idx];
                    const pct  = totalDraftSelections > 0 ? Math.round((wins / totalDraftSelections) * 100) : 0;
                    return (
                      <div key={idx} className="insights-draft-win-row">
                        <div className="insights-draft-win-label">
                          <span className="insights-draft-num">Draft {idx + 1}</span>
                          <span className="insights-draft-sublabel">{label}</span>
                        </div>
                        <div className="insights-bar-track">
                          <div className="insights-draft-win-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="insights-bar-count">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* What's winning for you — dynamic play labels (#2) */}
            {topPlaySelections >= 3 && (
              <div className="insights-section">
                <div className="insights-section-header">
                  <h3 className="insights-section-heading">What's winning for you</h3>
                  <div className="insights-legend">
                    <span className="insights-legend-item"><span className="insights-legend-dot insights-legend-dot--volume" />Times chosen</span>
                    <span className="insights-legend-item"><span className="insights-legend-dot insights-legend-dot--rate" />Reply rate</span>
                  </div>
                </div>
                <div className="insights-plays">
                  {winningPlays.slice(0, 6).map(play => {
                    const sharePct = topPlaySelections > 0 ? Math.round((play.selected / topPlaySelections) * 100) : 0;
                    const replyPct = play.sent > 0 ? Math.round((play.replied / play.sent) * 100) : null;
                    const replyColor = replyPct === null
                      ? 'var(--fg-4)'
                      : replyPct >= 30
                      ? 'var(--accent)'
                      : replyPct >= 15
                      ? 'var(--warn)'
                      : 'var(--terracotta-700)';
                    return (
                      <div key={play.tone} className="insights-play-row">
                        <span className="insights-play-label">{play.tone}</span>
                        <div className="insights-bar-track">
                          <div className="insights-play-fill" style={{ width: `${sharePct}%` }} />
                        </div>
                        <span className="insights-play-count">{play.selected}×</span>
                        <span className="insights-play-reply" style={{ color: replyColor }}>
                          {replyPct === null ? '—' : `${replyPct}%`}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="insights-plays-note">
                  The play label is what Claude picked for the draft you chose. Reply rate counts leads
                  that responded after you sent that draft.
                </p>
              </div>
            )}

            {/* Draft Preferences */}
            {drafts.length > 0 && (
              <div className="insights-section">
                <h3 className="insights-section-heading">Draft preferences</h3>
                <div className="insights-opts-grid insights-opts-grid--2col">
                  <OptionsBlock label="Tone" counts={toneCounts} />
                  <OptionsBlock label="Goal" counts={goalCounts} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

function OptionsBlock({ label, counts }: { label: string; counts: Record<string, number> }) {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const total   = entries.reduce((s, [, n]) => s + n, 0);
  if (entries.length === 0) return null;
  return (
    <div className="insights-opts-block">
      <span className="insights-opts-label">{label}</span>
      <div className="insights-opts-pills">
        {entries.map(([key, count]) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={key} className="insights-opt-pill">
              <span className="insights-opt-key">{key}</span>
              <div className="insights-opt-bar-track">
                <div className="insights-opt-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="insights-opt-pct">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
